// hooks/useBackdropClose.js
//
// Click-outside-to-close for a modal backdrop, without the drag-release bug.
//
// A bare `onClick={close}` on the backdrop closes far more often than intended,
// because a `click` fires on the nearest common ancestor of mousedown and
// mouseup. Select text in the notes field and release past the modal edge, drag a
// slider slightly too far, or let go of a range selection outside the box, and
// the browser reports that as a click ON THE BACKDROP — so the modal closes
// mid-edit, with no obvious cause. On the scheduler's edit/create modal, which
// has date fields, per-day hour inputs and a notes textarea, that is easy to hit
// by accident and reads exactly like bug report #12: "sometimes when I open a New
// Entry, it will close the window without any input from me."
//
// Fix: require the gesture to both START and END on the backdrop itself.
// mousedown bubbles, so a press that begins anywhere inside the modal clears the
// latch even though the later click retargets to the backdrop.
//
// Usage — spread onto the backdrop element, and DON'T pass onClick separately:
//   const backdrop = useBackdropClose(closeModal);
//   <div className="…-backdrop" {...backdrop}>…</div>
//
// The inner modal's `onClick={e => e.stopPropagation()}` is no longer required
// (the target check covers it) but is harmless where it already exists.

import { useRef, useCallback, useMemo } from 'react';

export function useBackdropClose(onClose, { enabled = true } = {}) {
  const armed = useRef(false);

  const onMouseDown = useCallback((e) => {
    // True only when the press landed on the backdrop itself, not on anything
    // inside the modal that bubbled up to it.
    armed.current = e.target === e.currentTarget;
  }, []);

  const onClick = useCallback((e) => {
    const wasArmed = armed.current;
    armed.current = false;
    if (!enabled) return;
    if (e.target !== e.currentTarget) return; // click originated inside the modal
    if (!wasArmed) return;                    // press started inside — a drag-release
    onClose?.(e);
  }, [onClose, enabled]);

  return useMemo(() => ({ onMouseDown, onClick }), [onMouseDown, onClick]);
}

export default useBackdropClose;
